/* Ask Shopify for the whole cart rate, including split fulfillment. Never
   infer the total from product classes or sum alternative delivery methods. */
class CartShippingQuote extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-shipping-quote-status]');
    this.button = this.querySelector('button[type="submit"]');
    if (!this.form || !this.status || !this.button) return;
    this.hidden = false;
    this.onSubmit = (event) => { event.preventDefault(); this.calculate(); };
    this.onInput = () => this.reset();
    this.form.addEventListener('submit', this.onSubmit);
    this.form.addEventListener('input', this.onInput);
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.reset());
    this.reset();
  }

  disconnectedCallback() {
    this.reset();
    this.form?.removeEventListener('submit', this.onSubmit);
    this.form?.removeEventListener('input', this.onInput);
    this.unsubscribe?.();
  }

  reset() {
    this.request?.abort();
    this.request = null;
    if (this.status) this.status.textContent = '';
    if (this.button) this.button.disabled = false;
    this.removeAttribute('aria-busy');
    this.removeAttribute('data-quoted');
  }

  async calculate() {
    this.reset();
    const zip = this.form.elements.zip.value.trim();
    if (!zip || !this.form.reportValidity()) return;
    const request = new AbortController();
    this.request = request;
    const timeout = setTimeout(() => request.abort(), 15000);
    this.button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = this.dataset.loading;
    const params = new URLSearchParams({
      'shipping_address[country]': this.dataset.country,
      'shipping_address[zip]': zip,
    });
    const fetchJSON = async (path, method = 'GET') => {
      const response = await fetch(`${this.dataset.cartUrl}/${path}.json?${params}`, {
        method, credentials: 'same-origin', signal: request.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Shipping rate request failed');
      return response.json();
    };
    try {
      await fetchJSON('prepare_shipping_rates', 'POST');
      let result = null;
      for (let attempt = 0; attempt < 10; attempt += 1) {
        if (attempt) await new Promise((resolve) => setTimeout(resolve, 1000));
        if (request.signal.aborted) throw new Error('Shipping rate request cancelled');
        result = await fetchJSON('async_shipping_rates');
        if (result !== null) break;
      }
      if (this.request !== request || !this.isConnected) return;
      if (!result || !Array.isArray(result.shipping_rates)) throw new Error('No rate result');
      if (!result.shipping_rates.length) {
        this.status.textContent = this.dataset.noRates;
        return;
      }
      const rates = result.shipping_rates.map((rate) => {
        const price = Number(rate.price);
        if (rate.price === null || rate.price === '' || !Number.isFinite(price) || price < 0 ||
          (rate.currency && rate.currency !== this.dataset.currency)) throw new Error('Invalid rate');
        return price;
      });
      const amount = new Intl.NumberFormat(document.documentElement.lang || 'de', {
        style: 'currency', currency: this.dataset.currency,
      }).format(Math.min(...rates));
      const message = rates.length > 1 ? this.dataset.fromResult : this.dataset.result;
      this.status.textContent = message.replace('[amount]', amount).replace('[postcode]', zip);
      this.setAttribute('data-quoted', 'true');
    } catch {
      // An older response must never put a price back after a cart/address edit.
      if (this.request === request && this.isConnected) this.status.textContent = this.dataset.error;
    } finally {
      clearTimeout(timeout);
      if (this.request === request) {
        this.request = null;
        this.button.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('cart-shipping-quote')) customElements.define('cart-shipping-quote', CartShippingQuote);
